import React, { useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import Linkify from "react-linkify";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Divider
} from '@material-ui/core';

import Alert from "../../utils/alert";
import { incrementViewCount } from "../../actions/jobActions";
import { getJobDetails } from "../../actions/jobDetailActions";
import { saveJob } from "../../actions/userActions";
import TagCloud from "../common/TagCloud";
import JobDetailCompanyInfo from "./JobDetail/JobDetailCompanyInfo";
import JobDetailLocation from "./JobDetail/JobDetailLocation";
import CoverMessage from "../common/CoverMessage";
import PageLoading from "../common/PageLoading";
import Image from "../common/Image";
import { getNewNotificationNum } from "../../utils/getNotificationNum";
import CurrentJobCard from "../profile/BusinessProfileDetail/CurrentJobCard";
import { AreaTag, CategoryTag, EnglisTag, WorkTypeTag } from "./Tags";

const useStyles = makeStyles((theme) => ({
  jobDetail: {
    position: 'relative',
    minHeight: '100vh',
  },
  jobTitle: {
    fontSize: '3.5rem',
    textAlign: 'center',
    color: theme.palette.primary.main,
    margin: theme.spacing(4, 0),
    [theme.breakpoints.down('sm')]: {
      marginLeft: '2rem',
      fontSize: '2.4rem',
    },
  },
  cover: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  coverMessage: {
    border: `2px solid ${theme.palette.common.black}`,
    padding: '1.5rem',
    backgroundColor: theme.palette.common.white,
  },
  marginSmall: {
    margin: theme.spacing(1),
  },
  marginMedium: {
    margin: theme.spacing(2),
  },
  marginTopMedium: {
    marginTop: theme.spacing(4),
  },
  marginBottomMedium: {
    marginBottom: theme.spacing(4),
  },
  marginBottomSmall: {
    marginBottom: theme.spacing(1.5),
  },
  textWrap: {
    whiteSpace: 'pre-wrap',
  },
  jobDescription: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  jobInfo: {
    minWidth: '38rem',
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto',
    },
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  infoLabel: {
    display: 'inline-block',
    width: '10rem',
    fontWeight: 600,
  },
  addressSection: {
    backgroundColor: theme.palette.secondary.light,
    borderRadius: '5px',
    padding: theme.spacing(1),
  },
  jobImage: {
    margin: theme.spacing(1, 'auto'),
  },
  imageWrapper: {
    backgroundColor: theme.palette.common.black,
    width: '100%',
    padding: '5px',
    textAlign: 'center',
    '& img': {
      margin: 'auto',
      display: 'block',
      backgroundColor: theme.palette.common.white,
      maxHeight: '50rem',
      maxWidth: '100%',
    },
  },
  jobList: {
    maxWidth: '40rem',
  },
  tag: {
    margin: theme.spacing(0.5, 1),
    fontSize: '1.8rem',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '1.8rem',
    marginBottom: theme.spacing(4),
  },
  buttonLarge: {
    fontSize: '1.6rem',
    padding: '0.75rem 1.5rem',
  },
  container: {
    marginBottom: theme.spacing(1.5),
  },
  mainContent: {
    marginBottom: theme.spacing(2),
  },
  sidebar: {
    marginBottom: theme.spacing(2),
  },
  jobDetailGallery: {
    '& .image-gallery-slide': {
      backgroundColor: '#0a0a0a',
      border: 'solid 1px #0a0a0a',
    },
    '& .image-gallery-image': {
      textAlign: 'center',
    },
    '& .image-gallery-image img': {
      margin: 'auto',
      objectFit: 'contain',
      width: '100%',
      height: '40rem',
      [theme.breakpoints.down('sm')]: {
        height: '35rem',
      },
    },
  },
}));

function JobDetail(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const jobDetails = useSelector(state => state.jobDetails.jobDetails);
  const userJobList = useSelector(state => state.jobDetails.userJobList);
  const user = useSelector(state => state.user.currentUser);
  const notifications = useSelector(state => state.user.notifications);
  let notificationNum = getNewNotificationNum(notifications);
  const savedJobList = useSelector(state => state.user.savedJobList);
  const jobId = props.match.params.id;

  useEffect(() => {
    dispatch(getJobDetails(jobId));
    dispatch(incrementViewCount(jobId));
  }, [dispatch, jobId]);

  const handleSaveJob = jobId => {
    dispatch(saveJob(user._id, jobId));
    Alert.success("保存リストに追加しました");
  };

  const renderApplyButton = (jobId, savedJob) => {
    if (savedJob && savedJob.applied) {
      return (
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled
          className={`${classes.buttonLarge} ${classes.marginSmall}`}
        >
          応募済み
        </Button>
      );
    } else {
      return (
        <Button
          component={Link}
          to={`/jobs/${jobId}/apply`}
          variant="contained"
          color="primary"
          size="large"
          className={`${classes.buttonLarge} ${classes.marginSmall}`}
        >
          応募する
        </Button>
      );
    }
  };

  const renderJobList = () => {
    if (userJobList.length === 0) {
      return null;
    }

    var contents = userJobList.map(job => {
      return (
        <Link to={`/jobs/${job._id}`} key={job._id}>
          <CurrentJobCard job={job} />
        </Link>
      );
    });

    return (
      <Fragment>
        <Typography variant="h3" className="section-title">
          投稿者の他の求人
        </Typography>
        <div className={classes.jobList}>{contents}</div>
      </Fragment>
    );
  };

  const renderTools = job => {
    if (!user) {
      return (
        <Typography variant="h4" className={classes.errorText}>
          <strong>求人応募フォームを利用するにはログインが必要です</strong>
        </Typography>
      );
    }

    if (user.profile.user_type === "Business") {
      if (user._id === job.user._id) {
        return (
          <Button
            component={Link}
            to={`/jobs/edit/${job._id}`}
            variant="contained"
            color="primary"
            size="large"
            className={classes.buttonLarge}
          >
            編集する
          </Button>
        );
      }
    } else {
      let savedJob = savedJobList
        ? savedJobList.find(savedJob => savedJob.job === job._id)
        : [];
      return (
        <Fragment>
          {savedJob ? (
            <Button
              variant="contained"
              color="secondary"
              size="large"
              disabled
              className={`${classes.buttonLarge} ${classes.marginSmall}`}
            >
              保存済み
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSaveJob(job._id)}
              className={`${classes.buttonLarge} ${classes.marginSmall}`}
              style={{
                backgroundColor: '#ffdd57',
                color: '#946200',
                '&:hover': {
                  backgroundColor: '#ffd700',
                }
              }}
            >
              保存リストに追加
            </Button>
          )}
          {job.email ? renderApplyButton(job._id, savedJob) : null}
          {job.phone ? (
            <Button
              variant="contained"
              color="info"
              size="large"
              href={`tel:${job.phone}`}
              className={`${classes.buttonLarge} ${classes.marginSmall}`}
            >
              電話する
            </Button>
          ) : null}
        </Fragment>
      );
    }
    return null;
  };

  const renderJobDetail = job => {
    if (!job) return <Typography>No data</Typography>;

    return (
      <Fragment>
        <Helmet>
          <title>{notificationNum ? `(${notificationNum})` : ""}求人詳細</title>
          <meta
            name="description"
            content="求人情報の詳細です。仕事内容、雇用者情報、雇用形態・特典等をチェックできます。"
          />
        </Helmet>
        <Typography variant="h1" className={classes.jobTitle}>
          {job.title}
        </Typography>
        <Container>
          {job.mainImage ? (
            <Box className={classes.jobImage}>
              <Box className={classes.imageWrapper}>
                <Image src={job.mainImage.image_url} />
              </Box>
            </Box>
          ) : null}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box className={classes.mainContent}>
                <Box className={`${classes.textWrap} ${classes.jobDescription}`}>
                  <Linkify>{job.details}</Linkify>
                </Box>
                {job.address ? (
                  <Box className={classes.addressSection}>
                    <Typography variant="h3">住所</Typography>
                    <JobDetailLocation
                      address={job.address}
                      jobLocation={job.location}
                    />
                  </Box>
                ) : null}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box className={classes.sidebar}>
                <Box className={`${classes.jobInfo} ${classes.marginBottomMedium}`}>
                  <Box className={classes.infoItem}>
                    <Typography variant="h5" className={classes.infoLabel}>
                      エリア:
                    </Typography>
                    <AreaTag content={job.area ? job.area.name : "エリア未設定"} size="medium" />
                  </Box>
                  <Box className={classes.infoItem}>
                    <Typography variant="h5" className={classes.infoLabel}>
                      職種:
                    </Typography>
                    <CategoryTag content={job.jobCategory} size="medium" />
                  </Box>
                  {job.workType ? (
                    <Box className={classes.infoItem}>
                      <Typography variant="h5" className={classes.infoLabel}>
                        雇用タイプ:
                      </Typography>
                      <WorkTypeTag content={job.workType} size="medium" />
                    </Box>
                  ) : null}
                  <Box className={classes.infoItem}>
                    <Typography variant="h5" className={classes.infoLabel}>
                      必要英語力:
                    </Typography>
                    <EnglisTag content={job.englishLevel ? job.englishLevel : "不問"} size="medium" />
                  </Box>
                </Box>
                {job.tags.length > 0 ? (
                  <Box className={classes.marginBottomMedium}>
                    <TagCloud
                      tags={job.tags} size="medium"
                    />
                  </Box>
                ) : null}
                <JobDetailCompanyInfo user={job.user} />
                {renderJobList()}
              </Box>
            </Grid>
          </Grid>
        </Container>
        <Container className={classes.container}>
          <Divider />
          {renderTools(job)}
        </Container>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <PageLoading animationClass="fade" />
      <Box component="section" className={classes.jobDetail}>
        {renderJobDetail(jobDetails)}
        {jobDetails && !jobDetails.is_active ? (
          <CoverMessage message="この求人は現在募集していません" />
        ) : null}
      </Box>
    </Fragment>
  );
}

export default JobDetail;
